import { MOCK_CAR_MODELS, type CarModel } from '@/app/lib/mock-db';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

export type CarTypeOption = {
  id: string;
  code: string;
  name_ar: string;
  name_en: string;
  image_url: string | null;
  is_active: boolean;
  sort_order: number;
};

const FALLBACK_TYPES: CarTypeOption[] = [
  { id: 'sedan', code: 'sedan', name_ar: 'سيدان', name_en: 'Sedan', image_url: '/assets/car_types/sedan.png', is_active: true, sort_order: 1 },
  { id: 'suv', code: 'suv', name_ar: 'دفع رباعي', name_en: 'SUV', image_url: '/assets/car_types/suv.png', is_active: true, sort_order: 2 },
  { id: 'pickup', code: 'pickup', name_ar: 'بيك أب', name_en: 'Pickup', image_url: '/assets/car_types/pickup.png', is_active: true, sort_order: 3 },
  { id: 'hatchback', code: 'hatchback', name_ar: 'هاتشباك', name_en: 'Hatchback', image_url: '/assets/car_types/hatchback.png', is_active: true, sort_order: 4 },
  { id: 'coupe', code: 'coupe', name_ar: 'كوبيه', name_en: 'Coupe', image_url: '/assets/car_types/coupe.png', is_active: true, sort_order: 5 },
  { id: 'van', code: 'van', name_ar: 'فان', name_en: 'Van', image_url: '/assets/car_types/van.png', is_active: true, sort_order: 6 },
  { id: 'convertible', code: 'convertible', name_ar: 'مكشوفة', name_en: 'Convertible', image_url: '/assets/car_types/convertible.png', is_active: true, sort_order: 7 },
  { id: 'luxury', code: 'luxury', name_ar: 'فاخر', name_en: 'Luxury', image_url: '/assets/car_types/luxury.png', is_active: true, sort_order: 8 },
  { id: 'sport', code: 'sport', name_ar: 'رياضي', name_en: 'Sport', image_url: '/assets/car_types/sport.png', is_active: true, sort_order: 9 },
  { id: 'truck', code: 'truck', name_ar: 'شاحنة', name_en: 'Truck', image_url: '/assets/car_types/truck.png', is_active: true, sort_order: 10 },
  { id: 'electric', code: 'electric', name_ar: 'كهربائية', name_en: 'Electric', image_url: '/assets/car_types/electric.png', is_active: true, sort_order: 11 },
];

function resolveAssetUrl(raw: unknown): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const value = raw.trim();
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  if (value.startsWith('/')) return `${API}${value}`;
  return null;
}

function normalizeType(raw: any, idx: number): CarTypeOption | null {
  const code = String(raw?.code || raw?.id || '').trim();
  if (!code) return null;
  return {
    id: code,
    code,
    name_ar: String(raw?.name_ar || raw?.name || `نوع ${code}`).trim(),
    name_en: String(raw?.name || raw?.name_en || raw?.name_ar || `Type ${code}`).trim(),
    image_url: resolveAssetUrl(raw?.image_url ?? raw?.image),
    is_active: raw?.is_active !== false,
    sort_order: Number(raw?.sort_order ?? idx + 1),
  };
}

function normalizeKey(value: unknown): string {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9\u0600-\u06ff]/g, '');
}

function localTypeImage(type: CarTypeOption): string | null {
  const key = normalizeKey(type.code || type.id || type.name_en);
  const match = FALLBACK_TYPES.find((fallback) => {
    const fallbackKeys = [fallback.id, fallback.code, fallback.name_en, fallback.name_ar].map(normalizeKey);
    return fallbackKeys.includes(key);
  });
  return type.image_url || match?.image_url || null;
}

function mergeTypes(primary: CarTypeOption[], fallback: CarTypeOption[]): CarTypeOption[] {
  const merged: CarTypeOption[] = [];
  const seen = new Set<string>();

  const addType = (type: CarTypeOption) => {
    const keys = [type.id, type.code, type.name_en, type.name_ar].map(normalizeKey).filter(Boolean);
    if (keys.some((key) => seen.has(key))) return;
    keys.forEach((key) => seen.add(key));
    merged.push({ ...type, image_url: localTypeImage(type) });
  };

  primary.forEach(addType);
  fallback.forEach(addType);
  return merged.sort((a, b) => (a.sort_order - b.sort_order) || a.name_en.localeCompare(b.name_en));
}

function normalizeModel(raw: any, idx: number): CarModel | null {
  const code = String(raw?.code || raw?.id || '').trim();
  if (!code) return null;
  const makeId = String(raw?.make_id || raw?.make || '').trim();
  if (!makeId) return null;
  return {
    id: code,
    make_id: makeId,
    name_ar: String(raw?.name_ar || raw?.name || `موديل ${code}`).trim(),
    name_en: String(raw?.name || raw?.name_en || raw?.name_ar || `Model ${code}`).trim(),
    is_active: raw?.is_active !== false,
    sort_order: Number(raw?.sort_order ?? idx + 1),
  };
}

export async function fetchCarTypes(): Promise<CarTypeOption[]> {
  try {
    const res = await fetch(`${API}/api/marketplace/types/?is_active=true`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return FALLBACK_TYPES;
    const payload = await res.json();
    const rows = Array.isArray(payload) ? payload : (payload?.results || []);
    const normalizedTypes = rows
      .map(normalizeType)
      .filter((t: CarTypeOption | null): t is CarTypeOption => Boolean(t));
    const types = normalizedTypes.filter((t: CarTypeOption) => t.is_active);
    return mergeTypes(types, FALLBACK_TYPES);
  } catch {
    return FALLBACK_TYPES;
  }
}

export async function fetchCarModels(makeId?: string): Promise<CarModel[]> {
  try {
    const params = new URLSearchParams();
    params.set('is_active', 'true');
    if (makeId) params.set('make_id', makeId);
    const res = await fetch(`${API}/api/marketplace/models/?${params.toString()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      return makeId ? MOCK_CAR_MODELS.filter((m) => m.make_id === makeId) : MOCK_CAR_MODELS;
    }
    const payload = await res.json();
    const rows = Array.isArray(payload) ? payload : (payload?.results || []);
    const normalizedModels = rows
      .map(normalizeModel)
      .filter((m: CarModel | null): m is CarModel => Boolean(m));
    const models = normalizedModels
      .filter((m: CarModel) => m.is_active)
      .sort((a: CarModel, b: CarModel) => (a.sort_order - b.sort_order) || a.name_en.localeCompare(b.name_en));

    if (models.length > 0) return models;
    return makeId ? MOCK_CAR_MODELS.filter((m) => m.make_id === makeId) : MOCK_CAR_MODELS;
  } catch {
    return makeId ? MOCK_CAR_MODELS.filter((m) => m.make_id === makeId) : MOCK_CAR_MODELS;
  }
}
